/**
 * Picks how heavily to dim a banner image, given the image's measured
 * brightness (0 = black, 255 = white) from the API.
 *
 * Every banner used to sit under a flat 60% black scrim, which is fine
 * over a bright picture but leaves an already-dark one barely visible.
 * The scrim exists to keep the overlaid title readable, so it can't just
 * be removed - instead a dark image gets less of it and a bright image
 * keeps roughly what it had.
 */

/** What an unmeasured image gets: exactly the old fixed behaviour. */
export const DEFAULT_OVERLAY_ALPHA = 0.6;

/** Darkest images still need some scrim for white text to read against. */
const MIN_ALPHA = 0.25;
const MAX_ALPHA = 0.6;

/**
 * Below this the image is dark enough to need the lightest scrim, above
 * it the heaviest; in between it scales. Chosen rather than scaling
 * across the full 0-255 range because real backgrounds cluster well away
 * from the extremes, and using the whole range would barely move the
 * alpha for the images that actually have the problem.
 */
const DARK_POINT = 40;
const BRIGHT_POINT = 140;

export const overlayAlphaForBrightness = (
  brightness?: number | null,
): number => {
  if (typeof brightness !== 'number' || Number.isNaN(brightness)) {
    return DEFAULT_OVERLAY_ALPHA;
  }
  const clamped = Math.min(Math.max(brightness, 0), 255);
  const position =
    (clamped - DARK_POINT) / (BRIGHT_POINT - DARK_POINT);
  const eased = Math.min(Math.max(position, 0), 1);
  return Number((MIN_ALPHA + (MAX_ALPHA - MIN_ALPHA) * eased).toFixed(3));
};

/**
 * The style object to spread onto a banner element. Returns the custom
 * property the overlay mixin reads.
 */
export const bannerOverlayStyle = (brightness?: number | null) =>
  ({
    '--banner-overlay-alpha': overlayAlphaForBrightness(brightness),
  }) as React.CSSProperties;

/**
 * The brightness of whichever image a banner actually ends up showing.
 *
 * The pages fall back background -> cover -> photo/image depending on
 * what a given record has, and the API sends a brightness alongside each
 * of those, so this walks the same ladder to stay in step. Returns
 * undefined when the chosen image has not been measured, which leaves
 * the overlay at its previous fixed value.
 */
export const bannerBrightness = (item?: {
  background?: string;
  backgroundBrightness?: number;
  cover?: string;
  coverBrightness?: number;
  photo?: string;
  photoBrightness?: number;
  image?: string;
  imageBrightness?: number;
}): number | undefined => {
  if (!item) return undefined;
  if (item.background) return item.backgroundBrightness;
  if (item.cover) return item.coverBrightness;
  if (item.photo) return item.photoBrightness;
  if (item.image) return item.imageBrightness;
  return undefined;
};
